import { Box, Button, Typography, alpha } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import type { DashboardSection, SectionId } from '../sections';

interface SectionFiltersProps {
  /** Sections the user is permission-gated into seeing. */
  sections: DashboardSection[];
  /** IDs currently toggled on. */
  visibleSet: Set<SectionId>;
  onToggle: (id: SectionId) => void;
  onShowAll: () => void;
}

/**
 * Chip-row that toggles which gated dashboard sections render. Ports the
 * prototype's `SectionFilters` (`dashboard-v2.jsx`) onto v2 MUI tokens.
 *
 * Rendering is **gated × filtered**: only sections the user has permission
 * for appear here, and toggling one off only hides it locally for the
 * current browser (persisted via `useSectionFilters`). Never reveals a
 * section the permission gate excludes (absent-not-masked).
 *
 * The container hides itself when the user has access to ≤2 sections —
 * the prototype's "consultant with three always-on sections" case has no
 * meaningful filter and the chip row would be visual noise. That branch
 * is owned by `useSectionFilters.showChipRow`; this component assumes the
 * caller has already decided to render it.
 */
export function SectionFilters({
  sections,
  visibleSet,
  onToggle,
  onShowAll,
}: SectionFiltersProps) {
  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'wrap',
        px: 1.75,
        py: 1.25,
        mb: 2,
        backgroundColor: 'background.paper',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1.5,
        boxShadow: theme.shadows[1],
      })}
      role="group"
      aria-label="Dashboard section filters"
    >
      <Typography
        component="span"
        sx={{
          fontSize: '0.6875rem',
          fontWeight: 600,
          color: 'text.disabled',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          mr: 0.5,
        }}
      >
        Show
      </Typography>

      {sections.map((section) => {
        const on = visibleSet.has(section.id);
        return (
          <Box
            key={section.id}
            component="button"
            type="button"
            onClick={() => onToggle(section.id)}
            aria-pressed={on}
            aria-label={`${on ? 'Hide' : 'Show'} ${section.label} section`}
            sx={(theme) => ({
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.25,
              py: 0.625,
              fontSize: '0.75rem',
              fontWeight: 600,
              fontFamily: 'inherit',
              lineHeight: 1,
              borderRadius: 999,
              cursor: 'pointer',
              backgroundColor: on
                ? theme.palette.accent.tint
                : theme.palette.surface.raised,
              color: on ? theme.palette.primary.main : theme.palette.text.disabled,
              border: `1px solid ${
                on ? theme.palette.primary.main : theme.palette.divider
              }`,
              transition: theme.transitions.create(
                ['background-color', 'color', 'border-color'],
                { duration: theme.transitions.duration.shortest },
              ),
              '&:hover': {
                backgroundColor: on
                  ? theme.palette.accent.tint
                  : alpha(theme.palette.text.primary, 0.04),
              },
              '&:focus-visible': {
                outline: 'none',
                boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
              },
              '& > svg': {
                fontSize: '0.875rem',
              },
            })}
          >
            {section.icon}
            <span>{section.label}</span>
            {on && <CheckIcon sx={{ fontSize: '0.8125rem' }} />}
          </Box>
        );
      })}

      <Box sx={{ flex: 1, minWidth: 8 }} />

      <Button
        size="small"
        variant="text"
        onClick={onShowAll}
        sx={{
          fontSize: '0.6875rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          minWidth: 0,
          px: 1,
        }}
      >
        Show all
      </Button>
    </Box>
  );
}
